/**
 * Owner-side availability hint for a published presentation (#1762).
 *
 * The backend reports what an anonymous visitor would get right now
 * (`GET /api/v1/presentations/{id}/availability`); this module turns that into an
 * observation the editor can show. Rules agreed in Post #4963, t-25569cc5f3e1:
 *  - a check runs only on open, save, publication change or a deliberate refresh;
 *    this controller owns no timers and never polls;
 *  - a transport or server error is reported as "check failed", never as a status;
 *  - the observation is a snapshot with a timestamp, not a release guarantee.
 */

export type AnonymousAvailability = 'available' | 'review_pending' | 'unavailable' | 'not_public';

export interface AvailabilityReport {
    isPublic: boolean;
    anonymous: AnonymousAvailability;
    /** ISO timestamp reported by the backend. */
    checkedAt: string;
}

export type AvailabilityTrigger = 'open' | 'save' | 'publication' | 'manual';

export type AvailabilityProbe = (draftId: string) => Promise<AvailabilityReport>;

export type AvailabilityObservation =
    | { kind: 'idle' }
    | { kind: 'checking'; trigger: AvailabilityTrigger }
    | { kind: 'observed'; trigger: AvailabilityTrigger; report: AvailabilityReport }
    | { kind: 'check_failed'; trigger: AvailabilityTrigger; message: string; at: string };

const IDLE: AvailabilityObservation = { kind: 'idle' };

/**
 * Sequential, token-guarded check state. A newer check supersedes an older one whose
 * result is still in flight; the stale result is dropped instead of overwriting.
 */
export class PresentationAvailabilityController {
    private observation: AvailabilityObservation = IDLE;
    private readonly listeners = new Set<() => void>();
    private requestToken = 0;

    constructor(
        private readonly probe: AvailabilityProbe,
        private readonly now: () => string = () => new Date().toISOString(),
    ) {
        this.subscribe = this.subscribe.bind(this);
        this.getSnapshot = this.getSnapshot.bind(this);
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    getSnapshot(): AvailabilityObservation {
        return this.observation;
    }

    /** Forget the last observation, e.g. after the draft was made private. */
    reset(): void {
        this.requestToken += 1;
        this.set(IDLE);
    }

    async check(draftId: string, trigger: AvailabilityTrigger): Promise<AvailabilityObservation> {
        const token = ++this.requestToken;
        this.set({ kind: 'checking', trigger });
        let next: AvailabilityObservation;
        try {
            const report = await this.probe(draftId);
            next = { kind: 'observed', trigger, report };
        } catch (reason) {
            // Any failure is "could not check" (Codex edge, #1762): no status is implied.
            const message = reason instanceof Error && reason.message ? reason.message : 'Verbindung fehlgeschlagen';
            next = { kind: 'check_failed', trigger, message, at: this.now() };
        }
        if (token !== this.requestToken) return this.observation; // superseded or reset
        this.set(next);
        return next;
    }

    private set(observation: AvailabilityObservation): void {
        this.observation = observation;
        this.listeners.forEach((listener) => listener());
    }
}

export type AvailabilityTone = 'ok' | 'warn' | 'error' | 'muted';

export interface AvailabilityMessage {
    tone: AvailabilityTone;
    text: string;
    /** Stable machine-readable state for tests: idle | checking | <anonymous> | check_failed. */
    state: string;
}

function clock(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

/** Wording for the editor. Observations, never promises about when something changes. */
export function describeAvailability(observation: AvailabilityObservation): AvailabilityMessage | null {
    switch (observation.kind) {
        case 'idle':
            return null;
        case 'checking':
            return { tone: 'muted', state: 'checking', text: 'Verfügbarkeit für Besucher wird geprüft …' };
        case 'check_failed':
            return {
                tone: 'error',
                state: 'check_failed',
                text: 'Prüfung nicht möglich: Ob der Link für Besucher abrufbar ist, konnte gerade nicht festgestellt werden.',
            };
        case 'observed': {
            const { report } = observation;
            const stamp = clock(report.checkedAt);
            const at = stamp ? ` · Stand ${stamp}` : '';
            switch (report.anonymous) {
                case 'available':
                    return { tone: 'ok', state: 'available', text: `Für Besucher abrufbar${at}` };
                case 'review_pending':
                    return {
                        tone: 'warn',
                        state: 'review_pending',
                        text: `Besucher sehen derzeit einen Hinweis, die Präsentation wird noch geprüft${at}`,
                    };
                case 'unavailable':
                    return { tone: 'warn', state: 'unavailable', text: `Für Besucher derzeit nicht abrufbar${at}` };
                case 'not_public':
                    return { tone: 'muted', state: 'not_public', text: `Nicht öffentlich${at}` };
                default:
                    return null;
            }
        }
        default:
            return null;
    }
}
